import { useRecoilCallback } from 'recoil';
import { PageType } from 'venn-utils';
import {
  type BlockId,
  blockContinuationPageCount,
  blockExpandedRows,
  blockExpandedState,
  blockOriginalLayoutState,
  continuationUserBlocksState,
  isContinuationLayoutId,
  makeContinuationLayoutId,
  planNeighborsAfterExpansion,
  RL_GRID_LANDSCAPE_NO_COLUMNS,
  RL_GRID_PORTRAIT_NO_COLUMNS,
  studioMaxUsableRows,
  studioPrintOrientationType,
  viewPages,
} from 'venn-state';
import type { Layout } from 'react-grid-layout';
import type { Page } from '../contexts/studio-context';

type UseExpandBlockResult = {
  expand: () => Promise<void>;
  contract: () => Promise<void>;
};

const findSourcePageIndex = (pages: readonly Page[], blockId: BlockId): number =>
  pages.findIndex((page) => page.type === PageType.GRID && page.layout.some((l) => l.i === blockId));

const restoreSourceLayout = (page: Page, blockId: BlockId, original: { x: number; w: number }): Page => ({
  ...page,
  layout: page.layout.map((l) => (l.i === blockId ? { ...l, x: original.x, w: original.w } : l)),
});

export const normalizeLayoutY = (layout: Layout): Layout => {
  if (layout.length === 0) return [];
  const minY = Math.min(...layout.map((l) => l.y));
  return layout.map((l) => ({ ...l, y: l.y - minY }));
};

/**
 * Returns the next state for a continuation page during contract:
 *   - `null`              → drop (page contained only the continuation placeholder)
 *   - `Page` (CONTINUATION) → leave as-is (continuation belongs to a different block)
 *   - `Page` (GRID)       → convert (page contained user-dropped blocks; promote to a regular grid page)
 */
export const reconcileContinuationOnContract = (page: Page, blockId: BlockId): Page | null => {
  if (page.type !== PageType.CONTINUATION) return page;
  if (page.continuation.sourceBlockId !== blockId) return page;

  const userBlocks = page.layout.filter((l) => !isContinuationLayoutId(l.i));
  if (userBlocks.length === 0) return null;

  return {
    type: PageType.GRID,
    layout: normalizeLayoutY(userBlocks),
  };
};

export const useExpandBlock = (blockId: BlockId): UseExpandBlockResult => {
  const expand = useRecoilCallback(
    ({ set, snapshot }) =>
      async (blockId: BlockId) => {
        const pages = await snapshot.getPromise(viewPages);
        const orientation = await snapshot.getPromise(studioPrintOrientationType);
        const noColumns = orientation === 'PORTRAIT' ? RL_GRID_PORTRAIT_NO_COLUMNS : RL_GRID_LANDSCAPE_NO_COLUMNS;

        const sourceIndex = findSourcePageIndex(pages, blockId);
        if (sourceIndex < 0) return;

        const sourcePage = pages[sourceIndex]!;
        const sourceLayout = sourcePage.layout.find((l) => l.i === blockId);
        if (!sourceLayout) return;

        const existingSnapshot = await snapshot.getPromise(blockOriginalLayoutState(blockId));
        if (!existingSnapshot) {
          set(blockOriginalLayoutState(blockId), { x: sourceLayout.x, w: sourceLayout.w });
        }

        const expandedRows = await snapshot.getPromise(blockExpandedRows(blockId));
        const maxRows = await snapshot.getPromise(studioMaxUsableRows);

        const { toKeep, stayBelow, spillover } = planNeighborsAfterExpansion({
          layout: sourcePage.layout,
          expandingBlockId: blockId,
          expandedRows,
          maxRows,
        });

        set(viewPages, (current) => {
          const next = current.map((page, idx) => {
            if (idx !== sourceIndex) return page;
            return {
              ...page,
              layout: [...toKeep.map((l) => (l.i === blockId ? { ...l, x: 0, w: noColumns } : l)), ...stayBelow],
            };
          });

          if (spillover.length === 0) return next;

          const spilloverPage: Page = {
            type: PageType.GRID,
            layout: normalizeLayoutY(spillover),
          };
          return [...next.slice(0, sourceIndex + 1), spilloverPage, ...next.slice(sourceIndex + 1)];
        });

        set(blockExpandedState(blockId), true);
      },
    [],
  );

  const contract = useRecoilCallback(
    ({ set, snapshot }) =>
      async (blockId: BlockId) => {
        // Read the pre-expand layout and continuation count up front (independent reads).
        // The count must be read before clearing expansion state, since
        // blockContinuationPageCount returns 0 once blockExpandedState is false.
        const [original, continuationCount] = await Promise.all([
          snapshot.getPromise(blockOriginalLayoutState(blockId)),
          snapshot.getPromise(blockContinuationPageCount(blockId)),
        ]);
        const continuationKeys = Array.from({ length: continuationCount }, (_, c) =>
          makeContinuationLayoutId({ sourceBlockId: blockId, continuationIndex: c }),
        );
        const userBlocksPerSlice = await Promise.all(
          continuationKeys.map((key) => snapshot.getPromise(continuationUserBlocksState(key))),
        );
        const allContinuationUserBlocks: Layout = userBlocksPerSlice.flat();

        set(viewPages, (current) => {
          const sourceIndex = findSourcePageIndex(current, blockId);

          const next: Page[] = [];
          let nextSourceIdx = -1;
          for (let i = 0; i < current.length; i++) {
            const page = current[i]!;
            const reconciled = i === sourceIndex && original ? restoreSourceLayout(page, blockId, original) : page;
            const continuationResult = reconcileContinuationOnContract(reconciled, blockId);
            if (continuationResult !== null) {
              if (i === sourceIndex) nextSourceIdx = next.length;
              next.push(continuationResult);
            }
          }

          // Promote any user-dropped blocks onto continuation-page to a new GRID page.
          if (allContinuationUserBlocks.length > 0 && nextSourceIdx >= 0) {
            const userPage: Page = { type: PageType.GRID, layout: normalizeLayoutY(allContinuationUserBlocks) };
            return [...next.slice(0, nextSourceIdx + 1), userPage, ...next.slice(nextSourceIdx + 1)];
          }
          return next;
        });

        // Clear the keyed store entries for this block's continuations.
        for (const key of continuationKeys) {
          set(continuationUserBlocksState(key), []);
        }

        set(blockOriginalLayoutState(blockId), undefined);
        set(blockExpandedState(blockId), false);
      },
    [],
  );

  return {
    expand: () => expand(blockId),
    contract: () => contract(blockId),
  };
};
